import React, { useState, useEffect } from 'react';
import { Plus, Users, Shield, Trash2, Layers, Copy, GitCompare, Workflow } from 'lucide-react';
import { roleService } from '../../services/roleService';
import { securitySettingsService } from '../../services/securitySettingsService';
import CreateRoleModal from './components/CreateRoleModal';
import EditRoleModal from './components/EditRoleModal';
import PermissionMatrix from './components/PermissionMatrix';
import BulkPermissionModal from './components/BulkPermissionModal';
import PermissionTemplateModal from './components/PermissionTemplateModal';
import RoleComparisonModal from './components/RoleComparisonModal';
import RoleHierarchyVisual from './components/RoleHierarchyVisual';
import PageUsageGuide from './components/PageUsageGuide';
import SecuritySettingsGuide from './components/SecuritySettingsGuide';
import { useAuth } from '../../contexts/AuthContext';
import { AdminGate } from '../../components/ui/PermissionGate';
import Button from '../../components/ui/Button';
import SecuritySettingsMatrix from './components/SecuritySettingsMatrix';


export default function RoleManagementDashboard() {
  const { isSuperAdmin, isBusinessAdmin, profile } = useAuth();
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showComparisonModal, setShowComparisonModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('roles');
  const [securityMatrix, setSecurityMatrix] = useState([]);
  const [loadingMatrix, setLoadingMatrix] = useState(false);

  useEffect(() => {
    loadRoles();
    if (activeTab === 'security') {
      loadSecurityMatrix();
    }
  }, [activeTab]);

  const loadRoles = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await roleService?.getAllRoles();
      setRoles(data);
      if (data?.length > 0 && !selectedRole) {
        setSelectedRole(data?.[0]);
      }
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const loadSecurityMatrix = async () => {
    try {
      setLoadingMatrix(true);
      const matrix = await securitySettingsService?.getSecurityMatrix(profile?.tenant_id);
      setSecurityMatrix(matrix);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoadingMatrix(false);
    }
  };

  const handleSecuritySettingChange = async (roleId, feature, isAllowed) => {
    try {
      await securitySettingsService?.updateSecuritySetting(
        roleId,
        feature,
        isAllowed,
        isSuperAdmin() ? null : profile?.tenant_id
      );
      await loadSecurityMatrix();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleResetToDefaults = async (roleId) => {
    if (!confirm('Reset all security settings to defaults for this role?')) return;

    try {
      await securitySettingsService?.resetToDefaults(
        roleId,
        isSuperAdmin() ? null : profile?.tenant_id
      );
      await loadSecurityMatrix();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleCreateRole = async (roleData) => {
    try {
      await roleService?.createRole(roleData);
      await loadRoles();
      setShowCreateModal(false);
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateRole = async (roleId, updates) => {
    try {
      await roleService?.updateRole(roleId, updates);
      await loadRoles();
      // Update selected role if it was the one being edited
      if (selectedRole?.id === roleId) {
        const updatedRole = await roleService?.getRoleById(roleId);
        setSelectedRole(updatedRole);
      }
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteRole = async (roleId) => {
    if (!confirm('Are you sure you want to delete this role?')) return;
    
    try {
      await roleService?.deleteRole(roleId);
      await loadRoles();
      if (selectedRole?.id === roleId) {
        setSelectedRole(roles?.[0]);
      }
    } catch (err) {
      setError(err?.message);
    }
  };

  const getRoleHierarchyBadge = (level) => {
    if (level <= 10) return { text: 'System Admin', color: 'bg-red-100 text-red-800' };
    if (level <= 20) return { text: 'Business Admin', color: 'bg-purple-100 text-purple-800' };
    if (level <= 40) return { text: 'Manager', color: 'bg-blue-100 text-blue-800' };
    if (level <= 60) return { text: 'Staff', color: 'bg-green-100 text-green-800' };
    return { text: 'Customer', color: 'bg-gray-100 text-gray-800' };
  };

  const filteredRoles = roles?.filter(role => {
    if (filter === 'all') return true;
    if (filter === 'system') return role?.isSystemRole;
    if (filter === 'custom') return !role?.isSystemRole;
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  const handleApplyTemplate = async (templateId) => {
    if (!selectedRole) {
      setError('Please select a role first');
      return;
    }

    try {
      await roleService?.applyTemplateToRole(selectedRole?.id, templateId);
      await loadRoles();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleSaveAsTemplate = async () => {
    if (!selectedRole) {
      setError('Please select a role first');
      return;
    }

    const name = prompt('Enter template name:');
    if (!name) return;

    const description = prompt('Enter template description (optional):');

    try {
      await roleService?.saveRoleAsTemplate(selectedRole?.id, name, description || '');
      alert('Template saved successfully!');
    } catch (err) {
      setError(err?.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Role Management</h1>
            <p className="text-gray-500 mt-1">
              {isSuperAdmin() 
                ? 'Manage roles and permissions across all organizations' :'Manage roles and permissions for your organization'}
            </p>
          </div>
          <AdminGate tenantId={profile?.tenant_id}>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowComparisonModal(true)}>
                <GitCompare className="w-4 h-4 mr-2" />
                Compare Roles
              </Button>
              <Button onClick={() => setShowCreateModal(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Role
              </Button>
            </div>
          </AdminGate>
        </div>

        {/* Tabs */}
        <div className="mt-6 border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('roles')}
              className={`${
                activeTab === 'roles' ?'border-indigo-500 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <Shield className="h-4 w-4" />
              Roles & Permissions
            </button>
            <button
              onClick={() => setActiveTab('hierarchy')}
              className={`${
                activeTab === 'hierarchy' ?'border-indigo-500 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <Layers className="h-4 w-4" />
              Hierarchy View
            </button>
            <button
              onClick={() => setActiveTab('usage')}
              className={`${
                activeTab === 'usage' ?'border-indigo-500 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <Workflow className="h-4 w-4" />
              Page Usage Guide
            </button>
            <button
              onClick={() => setActiveTab('security')}
              className={`${
                activeTab === 'security' ?'border-indigo-500 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <Shield className="h-4 w-4" />
              Security Settings
            </button>
            <button
              onClick={() => setActiveTab('guide')}
              className={`${
                activeTab === 'guide' ?'border-indigo-500 text-indigo-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2`}
            >
              <Workflow className="h-4 w-4" />
              Security Guide
            </button>
          </nav>
        </div>
      </div>
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'roles' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Roles List */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-lg shadow">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="text-lg font-semibold text-gray-900">Roles</h2>
                </div>
                <div className="divide-y divide-gray-200 max-h-[600px] overflow-y-auto">
                  {filteredRoles?.map((role) => {
                    const badge = getRoleHierarchyBadge(role?.hierarchyLevel);
                    return (
                      <div
                        key={role?.id}
                        onClick={() => setSelectedRole(role)}
                        className={`p-4 cursor-pointer hover:bg-gray-50 transition-colors ${
                          selectedRole?.id === role?.id ? 'bg-indigo-50' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <Shield className="w-4 h-4 text-gray-400 flex-shrink-0" />
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {role?.displayName}
                              </p>
                              {role?.isSystemRole && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                                  System
                                </span>
                              )}
                            </div>
                            <p className="mt-1 text-xs text-gray-500 line-clamp-2">
                              {role?.description}
                            </p>
                            <div className="mt-2 flex items-center space-x-3 text-xs text-gray-500">
                              <span className="flex items-center">
                                <Users className="w-3 h-3 mr-1" />
                                {role?.userCount} users
                              </span>
                              <span className={`px-2 py-0.5 rounded-full ${badge?.color}`}>
                                {badge?.text}
                              </span>
                            </div>
                          </div>
                          <AdminGate tenantId={profile?.tenant_id}>
                            <button
                              onClick={() => {
                                setSelectedRole(role);
                                setShowEditModal(true);
                              }}
                              className="text-primary hover:text-primary/80 mr-3"
                            >
                              Edit
                            </button>
                          </AdminGate>
                          {!role?.isSystemRole && (
                            <AdminGate tenantId={profile?.tenant_id}>
                              <button
                                onClick={() => handleDeleteRole(role?.id)}
                                className="text-error hover:text-error/80"
                              >
                                Delete
                              </button>
                            </AdminGate>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Permission Matrix */}
            <div className="lg:col-span-2">
              {selectedRole ? (
                <div className="bg-white rounded-lg shadow">
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center space-x-3">
                          <h2 className="text-xl font-semibold text-gray-900">
                            {selectedRole?.displayName}
                          </h2>
                          {selectedRole?.isSystemRole && (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              System Role
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-gray-500">
                          {selectedRole?.description}
                        </p>
                        <div className="mt-2 flex items-center space-x-4 text-sm text-gray-500">
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {selectedRole?.userCount} users assigned
                          </span>
                          <span>•</span>
                          <span>{selectedRole?.permissionCount} permissions</span>
                          {selectedRole?.overseesRoleIds?.length > 0 && (
                            <>
                              <span>•</span>
                              <span>Oversees {selectedRole?.overseesRoleIds?.length} role(s)</span>
                            </>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        {selectedRole?.canBeEdited !== false && (
                          <button
                            onClick={() => setShowEditModal(true)}
                            className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors flex items-center gap-1"
                            title="Edit Role"
                          >
                            <Shield className="w-3 h-3" />
                            Edit Role
                          </button>
                        )}
                        <button
                          onClick={handleSaveAsTemplate}
                          className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-1"
                          title="Save as Template"
                        >
                          <Copy className="w-3 h-3" />
                          Save as Template
                        </button>
                        {!selectedRole?.isSystemRole && (
                          <button
                            onClick={() => handleDeleteRole(selectedRole?.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                            title="Delete Role"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  <PermissionMatrix roleId={selectedRole?.id} />
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow p-12 text-center">
                  <Shield className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Select a Role
                  </h3>
                  <p className="text-gray-500">
                    Choose a role from the list to view and manage its permissions
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'hierarchy' && (
          <div className="bg-white rounded-lg shadow p-6">
            <RoleHierarchyVisual />
          </div>
        )}

        {activeTab === 'usage' && (
          <div className="bg-white rounded-lg shadow p-6">
            <PageUsageGuide />
          </div>
        )}

        {activeTab === 'security' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Dynamic Security Settings</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Control feature access for each role. Changes take effect immediately.
                  </p>
                </div>
                <AdminGate tenantId={profile?.tenant_id}>
                  <Button
                    variant="outline"
                    onClick={() => loadSecurityMatrix()}
                    disabled={loadingMatrix}
                  >
                    {loadingMatrix ? 'Refreshing...' : 'Refresh Matrix'}
                  </Button>
                </AdminGate>
              </div>
            </div>

            <div className="p-6">
              <SecuritySettingsMatrix
                securityMatrix={securityMatrix}
                onSettingChange={handleSecuritySettingChange}
                onResetToDefaults={handleResetToDefaults}
                loading={loadingMatrix}
                canEdit={isSuperAdmin() || isBusinessAdmin(profile?.tenant_id)}
              />
            </div>
          </div>
        )}

        {activeTab === 'guide' && (
          <SecuritySettingsGuide />
        )}
      </div>
      {/* Create Role Modal */}
      {showCreateModal && (
        <CreateRoleModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateRole}
          allRoles={roles}
        />
      )}
      {showBulkModal && (
        <BulkPermissionModal
          onClose={() => setShowBulkModal(false)}
          onApply={() => {
            loadRoles();
            setShowBulkModal(false);
          }}
          allRoles={roles}
        />
      )}
      {showTemplateModal && (
        <PermissionTemplateModal
          onClose={() => setShowTemplateModal(false)}
          onApply={handleApplyTemplate}
        />
      )}
      {showComparisonModal && (
        <RoleComparisonModal
          onClose={() => setShowComparisonModal(false)}
          allRoles={roles}
        />
      )}
      {showEditModal && selectedRole && (
        <EditRoleModal
          role={selectedRole}
          allRoles={roles}
          onClose={() => setShowEditModal(false)}
          onSave={handleUpdateRole}
        />
      )}
    </div>
  );
}